import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Space,
  Tabs,
  Descriptions,
  Statistic,
  Row,
  Col,
  Table,
  Tag,
  Modal,
  message,
  Tooltip,
  Alert
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  AndroidOutlined,
  AppleOutlined,
  GlobalOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  RobotOutlined, 
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchApplication,
  deleteApplication,
  fetchAppUsers,
  fetchAppUserStats,
  selectCurrentApplication,
  selectAppUsers,
  selectAppUserStats,
  selectApplicationsLoading,
} from 'store/slices/applications.slice';
import { usePermissions } from 'hooks/usePermissions';
import { AppUser, AppType } from 'types/application.types';
import { formatters } from 'utils/formatters';
import { ROUTES, generatePath } from 'config/routes';
import '../../styles/Common.css';

const { confirm } = Modal;
const getNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return value || null;
  if (typeof value === 'object' && value !== null) {
    const obj = value as Record<string, unknown>;
    if ('String' in obj && 'Valid' in obj) {
      return obj.Valid ? String(obj.String) : null;
    }
  }
  return null;
};

const ApplicationDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const permissions = usePermissions();

  const application = useAppSelector(selectCurrentApplication);
  const users = useAppSelector(selectAppUsers);
  const stats = useAppSelector(selectAppUserStats);
  const isLoading = useAppSelector(selectApplicationsLoading);

  const [activeTab, setActiveTab] = useState('info');

  useEffect(() => {
    if (id) {
      dispatch(fetchApplication(Number(id)));
      dispatch(fetchAppUserStats(Number(id)));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (activeTab === 'users' && id) {
      dispatch(fetchAppUsers({ appId: Number(id), filter: { limit: 20, offset: 0 } }));
    }
  }, [activeTab, id, dispatch]);

  const handleDelete = () => {
    if (!application) return;

    confirm({
      title: 'Удаление приложения',
      content: `Вы уверены, что хотите удалить "${application.name}"? Это действие нельзя отменить.`,
      okText: 'Удалить',
      cancelText: 'Отмена',
      okType: 'danger',
      onOk: async () => {
        try {
          await dispatch(deleteApplication(application.id)).unwrap();
          message.success('Приложение успешно удалено');
          navigate(ROUTES.APPLICATIONS);
        } catch {
          message.error('Не удалось удалить приложение');
        }
      },
    });
  };

  const getAppTypeIcon = (type: AppType) => {
    if (type === AppType.ANDROID_IOS) {
      return (
        <Space>
          <AndroidOutlined style={{ color: '#3ddc84', fontSize: 20 }} />
          <AppleOutlined style={{ color: '#000', fontSize: 20 }} />
        </Space>
      );
    }
    return <GlobalOutlined style={{ color: '#1890ff', fontSize: 20 }} />;
  };

  const usersColumns: ColumnsType<AppUser> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (value) => value ?? '-',
    },
    {
      title: 'External ID',
      dataIndex: 'user_external_id',
      key: 'user_external_id',
      width: 150,
      render: (value) => {
        const str = getNullableString(value);
        if (!str) return '-';
        const display = str.length > 12 ? `${str.slice(0, 12)}...` : str;
        return (
          <Tooltip title={str}>
            <code className="bundle-id">{display}</code>
          </Tooltip>
        );
      },
    },
    {
      title: 'OS',
      dataIndex: 'os',
      key: 'os',
      width: 80,
      render: (os) => {
        const osStr = String(os || '').toLowerCase();
        return (
          <Tag color={osStr === 'android' ? 'green' : osStr === 'ios' ? 'blue' : 'default'}>
            {osStr.toUpperCase() || '-'}
          </Tag>
        );
      },
    },
    {
      title: 'Локаль',
      dataIndex: 'locale',
      key: 'locale',
      width: 80,
      render: (locale) => String(locale || '-'),
    },
    {
      title: 'Регистрация',
      dataIndex: 'has_registration',
      key: 'has_registration',
      width: 120,
      render: (hasReg, record) => (
        <Space>
          {hasReg ? (
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
          )}
          {record.registration_date && (
            <span style={{ fontSize: 12, color: '#999' }}>
              {formatters.date(record.registration_date)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Депозит',
      dataIndex: 'has_deposit',
      key: 'has_deposit',
      width: 120,
      render: (hasDep, record) => (
        <Space>
          {hasDep ? (
            <CheckCircleOutlined style={{ color: '#52c41a' }} />
          ) : (
            <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
          )}
          {record.deposit_date && (
            <span style={{ fontSize: 12, color: '#999' }}>
              {formatters.date(record.deposit_date)}
            </span>
          )}
        </Space>
      ),
    },
    {
      title: 'Установлен',
      dataIndex: 'install_date',
      key: 'install_date',
      width: 120,
      render: (date) => date ? formatters.date(date) : '-',
    },
    {
      title: 'Статус',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Активен' : 'Удалён'}
        </Tag>
      ),
    },
  ];

  if (!application) {
    return (
      <div className="page-container">
        <Card loading={isLoading}>
          <div>Загрузка...</div>
        </Card>
      </div>
    );
  }

  const tabItems = [
    {
      key: 'info',
      label: 'Информация',
      children: (
        <div>
          {application.auto_created && !application.access_verified && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Сервисный аккаунт не имеет доступа к Firebase-проекту"
              description="Приложение заведено автоматически, пользователи собираются, но пуши отправляться не будут. Попросите клиента выдать сервисному аккаунту роль Firebase Cloud Messaging API Admin в своём проекте."
            />
          )}
          {stats && (
            <div className="stats-grid">
              <Card className="stat-card">
                <Statistic
                  title="Всего пользователей"
                  value={stats.total_users}
                  prefix={<UserOutlined />}
                  valueStyle={{ color: '#1890ff' }}
                />
              </Card>
              <Card className="stat-card">
                <Statistic
                  title="С регистрацией"
                  value={stats.with_registration}
                  suffix={`/ ${stats.total_users}`}
                  valueStyle={{ color: '#722ed1' }}
                />
              </Card>
              <Card className="stat-card">
                <Statistic
                  title="С депозитом"
                  value={stats.with_deposit}
                  suffix={`/ ${stats.total_users}`}
                  valueStyle={{ color: '#fa8c16' }}
                />
              </Card>
            </div>
          )}

          <Card title="Основная информация" className="detail-card">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Название">
                <strong>{application.name}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Происхождение">
                {application.auto_created ? (
                  <Tag icon={<RobotOutlined />} color="processing">Автоматически</Tag>
                ) : (
                  <Tag>Вручную</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Отправка данных об установке">
                <Tag color={application.send_install_data ? 'green' : 'default'}>
                  {application.send_install_data ? 'Включено' : 'Выключено'}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Bundle ID">
                <code className="bundle-id">{application.bundle_id}</code>
              </Descriptions.Item>
              <Descriptions.Item label="Тип">
                <Space>
                  {getAppTypeIcon(application.type)}
                  <span>
                    {application.type === AppType.ANDROID_IOS ? 'Мобильное приложение' : 'PWA'}
                  </span>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Создано">
                {formatters.dateTime(application.created_at)}
              </Descriptions.Item>
              <Descriptions.Item label="Обновлено">
                {formatters.dateTime(application.updated_at)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Firebase конфигурация" className="detail-card">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Project ID">
                {application.firebase_config.project_id}
              </Descriptions.Item>
              <Descriptions.Item label="Client Email">
                {application.firebase_config.client_email}
              </Descriptions.Item>
              <Descriptions.Item label="Bundle">
                {application.firebase_config.bundle}
              </Descriptions.Item>
              <Descriptions.Item label="Доступ к проекту">
                <Space>
                  {application.access_verified ? (
                    <Tag icon={<CheckCircleOutlined />} color="success">Подтверждён</Tag>
                  ) : (
                    <Tag icon={<WarningOutlined />} color="warning">Не подтверждён</Tag>
                  )}
                  {application.access_checked_at && (
                    <span style={{ fontSize: 12, color: '#999' }}>
                      проверен {formatters.dateTime(application.access_checked_at)}
                    </span>
                  )}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {stats && Object.keys(stats.by_os).length > 0 && (
            <Card title="Пользователи по платформам" className="detail-card">
              <Row gutter={16}>
                {Object.entries(stats.by_os).map(([os, count]) => (
                  <Col span={8} key={os}>
                    <Card>
                      <Statistic
                        title={os.toUpperCase()}
                        value={count}
                        prefix={
                          os === 'android' ? (
                            <AndroidOutlined style={{ color: '#3ddc84' }} />
                          ) : os === 'ios' ? (
                            <AppleOutlined />
                          ) : (
                            <GlobalOutlined />
                          )
                        }
                      />
                    </Card>
                  </Col>
                ))}
              </Row>
            </Card>
          )}
        </div>
      ),
    },
    {
      key: 'users',
      label: 'Пользователи',
      children: (
        <Card>
          <div style={{ marginBottom: 16 }}>
            <Button
              type="primary"
              onClick={() => navigate(generatePath(ROUTES.APPLICATION_USERS, { id: application.id }))}
            >
              Управление пользователями
            </Button>
          </div>
          <Table
            columns={usersColumns}
            dataSource={users}
            rowKey="id"
            loading={isLoading}
            pagination={{ pageSize: 10 }}
            scroll={{ x: 1000 }}
          />
        </Card>
      ),
    },
  ];

  return (
    <div className="page-container">
      <Card>
        <div className="page-header">
          <div className="page-header-content">
            <Space>
              <Button
                type="text"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate(ROUTES.APPLICATIONS)}
              />
              <div>
                <Space align="center" size={8}>
                  <h1 style={{ margin: 0 }}>{application.name}</h1>
                  {application.auto_created && (
                    <Tooltip title="Заведено автоматически при первом пользователе">
                      <Tag icon={<RobotOutlined />} color="processing">Автосоздано</Tag>
                    </Tooltip>
                  )}
                </Space>
                <p>Детальная информация о приложении</p>
              </div>
            </Space>
          </div>
          <div className="page-header-actions">
            <Space>
              {permissions.canEditApplications && (
                <Button
                  icon={<EditOutlined />}
                  onClick={() =>
                    navigate(generatePath(ROUTES.APPLICATION_EDIT, { id: application.id }))
                  }
                >
                  Редактировать
                </Button>
              )}
              {permissions.canDeleteApplications && (
                <Button danger icon={<DeleteOutlined />} onClick={handleDelete}>
                  Удалить
                </Button>
              )}
            </Space>
          </div>
        </div>

        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>
    </div>
  );
};

export default ApplicationDetail;